/**
 * Chemistry-check Kanban board. Cards are cases, one per column, auto-placed by
 * `columnFor` (no free drag — the state machine owns transitions, driven by
 * inbound email + the drawer's action buttons). Columns answer "whose court is
 * the ball in?" so a coordinator sees attention/response/hand-off at a glance.
 */

import { fmt } from '../lib/format';
import { CHEMISTRY_COLUMNS, bucketByColumn } from '../lib/chemistryBoard';
import type { ChemistryCase, ChemistryTrackState } from '../types';

interface Props {
  cases: ChemistryCase[];
  onOpenCase: (id: string) => void;
}

const TRACK_TONE: Record<ChemistryTrackState, string> = {
  waiting_client: 'var(--navy)',
  waiting_coach: 'var(--navy)',
  ready_to_schedule: '#5e35b1',
  scheduled: '#2e7d32',
  completion_unconfirmed: '#2d6a9e',
  complete: '#2e7d32',
  needs_human: 'var(--warn)',
};

const TRACK_STATE_LABEL: Record<ChemistryTrackState, string> = {
  waiting_client: 'Waiting for client',
  waiting_coach: 'Waiting for coach',
  ready_to_schedule: 'Ready to schedule',
  scheduled: 'Scheduled',
  completion_unconfirmed: 'Completion unconfirmed',
  complete: 'Complete',
  needs_human: 'Needs human',
};

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function scheduledCount(c: ChemistryCase): number {
  return c.tracks.filter(
    (t) => t.state === 'scheduled' || t.state === 'completion_unconfirmed' || t.state === 'complete',
  ).length;
}

export function ChemistryBoard({ cases, onOpenCase }: Props): JSX.Element {
  const now = Date.now();
  const buckets = bucketByColumn(cases);

  return (
    <div
      style={{
        flex: 1,
        overflowX: 'auto',
        overflowY: 'hidden',
        display: 'flex',
        gap: 12,
        padding: '12px 4px 16px',
        alignItems: 'stretch',
      }}
    >
      {CHEMISTRY_COLUMNS.map((col) => {
        const items = buckets[col.key];
        return (
          <div
            key={col.key}
            style={{
              flex: '0 0 268px',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              background: 'rgba(10,21,40,0.02)',
              border: '1px solid var(--border-1)',
            }}
          >
            <div style={{ borderTop: `2px solid ${col.accent}`, padding: '8px 10px', background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>{col.label}</span>
                <span className="d-tnum" style={{ fontSize: 12, color: 'var(--mute)' }}>{items.length}</span>
              </div>
              <div className="d-meta" style={{ fontSize: 10.5, color: 'var(--mute)', marginTop: 2 }}>{col.hint}</div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.length === 0 ? (
                <div className="d-meta" style={{ fontSize: 11, color: 'var(--border-2)', padding: '6px 4px' }}>—</div>
              ) : (
                items.map((c) => {
                  const done = scheduledCount(c);
                  const overdue = new Date(c.nextActionAt).getTime() < now;
                  const paused = c.waitingOn === 'Paused';
                  const accent = overdue ? 'var(--warn)' : col.accent;
                  return (
                    <div
                      key={c.id}
                      onClick={() => onOpenCase(c.id)}
                      style={{
                        cursor: 'pointer',
                        background: '#fff',
                        border: '1px solid var(--border-2)',
                        borderLeft: `3px solid ${accent}`,
                        padding: '8px 10px',
                        opacity: paused ? 0.6 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span className="mono" style={{ fontWeight: 600, fontSize: 12 }}>{c.tag}</span>
                        {c.live && (
                          <span className="pill" style={{ background: 'rgba(46,125,50,0.12)', color: '#2e7d32', fontSize: 9, padding: '1px 6px' }}>
                            <span className="pdot" />live
                          </span>
                        )}
                        {paused && (
                          <span className="pill" style={{ background: 'rgba(10,21,40,0.07)', color: 'var(--mute)', fontSize: 9, padding: '1px 6px' }}>paused</span>
                        )}
                      </div>

                      <div style={{ fontSize: 13, fontWeight: 500, marginTop: 3 }}>{c.clientName}</div>
                      {c.assistantName && (
                        <div className="d-meta" style={{ fontSize: 10.5 }}>via {c.assistantName} (EA)</div>
                      )}
                      <div className="d-meta" style={{ fontSize: 11, marginTop: 2 }}>
                        {c.pmName} · <span className="d-tnum">{fmt.date(c.targetDate)}</span>
                      </div>

                      {/* Per-coach chips — the "who has responded" signal */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {c.tracks.map((t) => (
                          <span
                            key={t.id}
                            title={`${t.coachName} — ${TRACK_STATE_LABEL[t.state]}`}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 10.5,
                              padding: '1px 6px',
                              border: '1px solid var(--border-1)',
                              background: '#fff',
                              color: 'var(--ink)',
                            }}
                          >
                            <span style={{ width: 6, height: 6, borderRadius: 99, background: TRACK_TONE[t.state] }} />
                            {firstName(t.coachName)}
                          </span>
                        ))}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6, gap: 6 }}>
                        <span className="d-tnum" style={{ fontSize: 11, color: 'var(--mute)' }}>{done}/{c.tracks.length} scheduled</span>
                        <span style={{ fontSize: 10.5, color: overdue ? 'var(--warn)' : 'var(--mute)', textAlign: 'right', flex: 1, minWidth: 0 }}>
                          {overdue ? 'due now · ' : ''}{c.nextAction}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
